import { useEffect, useMemo, useState } from "react";
import { EmptyState } from "@hybrid-iptv/ui";
import { usePlayerStore } from "../player/playerStore.js";
import {
  dvr,
  formatBytes,
  recordingErrorText,
  useDvr,
  volumeHoursLabel,
  type DvrRecording,
  type DvrRule,
} from "../dvr/dvr.js";
import { brand } from "../brand.js";
import { ProLockedPanel } from "../components/ProLockedPanel.js";
import { useFeatureUnlocked } from "../library/proFeatures.js";

/**
 * Recordings (Section 20, Android app only): what is recording now, what is
 * booked, series rules, finished recordings, and where recordings are stored.
 */
export function RecordingsPage() {
  // Recording is Pro (ADR 2026-10-10): the page says so instead of listing what cannot be made.
  const unlocked = useFeatureUnlocked("recordings");
  return unlocked ? <RecordingsContent /> : <ProLockedPanel feature="recordings" />;
}

function RecordingsContent() {
  const { available, state, refresh } = useDvr();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const groups = useMemo(() => {
    const all = state?.recordings ?? [];
    const by = (status: DvrRecording["status"][]) => all.filter((r) => status.includes(r.status));
    return {
      now: by(["recording"]),
      scheduled: by(["scheduled"]).sort((a, b) => a.startMs - b.startMs),
      planned: by(["planned"]).sort((a, b) => a.startMs - b.startMs),
      finished: by(["done", "partial"]).sort((a, b) => b.startMs - a.startMs),
      problems: all
        .filter((r) => r.status === "failed" || r.status === "skipped")
        .sort((a, b) => b.startMs - a.startMs),
    };
  }, [state]);

  // Sizes of recordings in progress: nothing announces each megabyte, so look again every few seconds.
  const recordingNow = groups.now.length > 0;
  useEffect(() => {
    if (!recordingNow) return;
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(timer);
  }, [recordingNow, refresh]);

  if (!available) {
    return (
      <div className="page page--recordings">
        <EmptyState title="Recordings" body="Scheduled recording is available in the SliqueTV Android app." icon="⏺" />
      </div>
    );
  }
  if (!state) {
    return <div className="page page--recordings"><p className="recordings__muted">Loading recordings…</p></div>;
  }

  const upcomingFor = (rule: DvrRule) => groups.scheduled.filter((r) => r.ruleId === rule.id).length;
  const plannedFor = (rule: DvrRule) => groups.planned.filter((r) => r.ruleId === rule.id).length;

  function play(recording: DvrRecording) {
    if (!recording.fileUri) return;
    void usePlayerStore.getState().playSource({
      url: recording.fileUri,
      type: "vod",
      title: recording.subtitle ? `${recording.title} — ${recording.subtitle}` : recording.title,
    });
  }

  function remove(recording: DvrRecording) {
    if (confirmDelete !== recording.id) {
      setConfirmDelete(recording.id);
      return;
    }
    setConfirmDelete(null);
    void dvr.remove(recording.id);
  }

  return (
    <div className="page page--recordings">
      <h1 className="recordings__title">Recordings</h1>

      {!state.exactAlarms && (
        <div className="recordings__warning" role="alert">
          <span>Alarms are turned off for {brand().appName}, so recordings may start late or not wake the device.</span>
          <button className="recordings__btn" onClick={() => void dvr.openAlarmSettings()}>Allow alarms</button>
        </div>
      )}

      <section className="recordings__section" aria-label="Storage">
        <h2>Save recordings to</h2>
        <div className="recordings__volumes">
          {state.volumes.map((volume) => (
            <button
              key={volume.id}
              className={volume.id === state.volumeId ? "recordings__volume is-active" : "recordings__volume"}
              aria-pressed={volume.id === state.volumeId}
              onClick={() => void dvr.setSettings({ volumeId: volume.id })}
            >
              <strong>{volume.label}</strong>
              <span>{formatBytes(volume.freeBytes)} free of {formatBytes(volume.totalBytes)}</span>
              <span>
                About {volumeHoursLabel(volume, state.assumedBps)} of HD recording · {formatBytes(volume.reserveBytes)} kept free
                {volume.removable ? "" : " for the box"}
              </span>
            </button>
          ))}
        </div>
        <p className="recordings__muted">
          Recordings are kept in {brand().appName}'s own folder on that storage, and Android removes them if the app is uninstalled.
          Starts {Math.round(state.padBeforeMs / 60000)} min early and runs {Math.round(state.padAfterMs / 60000)} min over.
          {state.maxConnections > 0 && ` Your provider allows ${state.maxConnections} stream${state.maxConnections === 1 ? "" : "s"} at a time.`}
        </p>
      </section>

      {groups.now.length > 0 && (
        <section className="recordings__section" aria-label="Recording now">
          <h2>Recording now</h2>
          {groups.now.map((r) => (
            <RecordingRow key={r.id} recording={r} detail={`${formatBytes(r.bytes)} so far`}>
              <button className="recordings__btn" onClick={() => void dvr.cancel(r.id)}>Stop</button>
            </RecordingRow>
          ))}
        </section>
      )}

      <section className="recordings__section" aria-label="Scheduled">
        <h2>Scheduled</h2>
        {groups.scheduled.length === 0 ? (
          <p className="recordings__muted">Nothing booked. Press and hold a programme in the guide, or open it, and choose Record.</p>
        ) : (
          groups.scheduled.map((r) => (
            <RecordingRow key={r.id} recording={r}>
              <button className="recordings__btn" onClick={() => void dvr.cancel(r.id)}>Cancel</button>
            </RecordingRow>
          ))
        )}
      </section>

      {state.rules.length > 0 && (
        <section className="recordings__section" aria-label="Series">
          <h2>Series</h2>
          {state.rules.map((rule) => (
            <div key={rule.id} className="recordings__row">
              <div className="recordings__info">
                <strong>{rule.title}</strong>
                <span>
                  {rule.channelName ? `On ${rule.channelName}` : "On any channel"} · {rule.newOnly ? "New episodes" : "Every airing"} ·{" "}
                  {rule.keep > 0 ? `Keep latest ${rule.keep}` : "Keep all"} · {upcomingFor(rule)} booked{plannedFor(rule) > 0 ? ` · ${plannedFor(rule)} coming up` : ""}
                </span>
              </div>
              <button className="recordings__btn" onClick={() => void dvr.removeRule(rule.id)}>Stop series</button>
            </div>
          ))}
          <p className="recordings__muted">
            {state.guideChecking
              ? "Checking the guide for episodes…"
              : state.lastGuideCheckAt > 0
                ? `Guide checked ${new Date(state.lastGuideCheckAt).toLocaleString()}${state.lastGuideCheckError ? " — couldn't read the guide" : ""}.` +
                  (state.guideHorizonMs > 0
                    ? ` Your guide covers until ${new Date(state.guideHorizonMs).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}, so episodes are booked as they appear in it.`
                    : "") +
                  " Series keep recording until you stop them; the guide is checked every 2 hours."
                : state.guideConfigured
                  ? "The guide will be checked for episodes shortly."
                  : "Open Live TV once so the app can learn your guide."}{" "}
            {!state.guideChecking && state.guideConfigured && (
              <button className="recordings__link" onClick={() => void dvr.checkGuideNow()}>Check now</button>
            )}
          </p>
        </section>
      )}

      {groups.planned.length > 0 && (
        <section className="recordings__section" aria-label="Coming up">
          <h2>Coming up</h2>
          <p className="recordings__muted">
            Upcoming episodes of your series from TV listings. Each is booked on your channel, at the exact time, once your provider's guide reaches it.
          </p>
          {groups.planned.map((r) => (
            <RecordingRow key={r.id} recording={r} detail={episodeLabel(r.episodeKey)}>
              <button className="recordings__btn" onClick={() => void dvr.cancel(r.id)}>Skip</button>
            </RecordingRow>
          ))}
        </section>
      )}

      <section className="recordings__section" aria-label="Recorded">
        <h2>Recorded</h2>
        {groups.finished.length === 0 ? (
          <p className="recordings__muted">No recordings yet.</p>
        ) : (
          groups.finished.map((r) => (
            <RecordingRow
              key={r.id}
              recording={r}
              detail={[formatBytes(r.bytes), r.status === "partial" ? recordingErrorText(r.error) : "", r.gaps > 0 ? `${r.gaps} reconnect${r.gaps === 1 ? "" : "s"}` : ""]
                .filter(Boolean)
                .join(" · ")}
            >
              <button className="recordings__btn recordings__btn--primary" onClick={() => play(r)} disabled={!r.fileUri}>▶ Play</button>
              <button className="recordings__btn" onClick={() => remove(r)}>{confirmDelete === r.id ? "Confirm delete" : "Delete"}</button>
            </RecordingRow>
          ))
        )}
      </section>

      {groups.problems.length > 0 && (
        <section className="recordings__section" aria-label="Didn't record">
          <h2>Didn't record</h2>
          {groups.problems.map((r) => (
            <RecordingRow key={r.id} recording={r} detail={recordingErrorText(r.error)}>
              <button className="recordings__btn" onClick={() => void dvr.remove(r.id)}>Dismiss</button>
            </RecordingRow>
          ))}
        </section>
      )}
    </div>
  );
}

/** "s2e5" → "S2 E5". */
function episodeLabel(key: string | undefined): string {
  const match = key?.match(/^s(\d+)e(\d+)$/);
  return match ? `S${match[1]} E${match[2]}` : "";
}

function RecordingRow({ recording, detail, children }: { recording: DvrRecording; detail?: string; children: React.ReactNode }) {
  const start = new Date(recording.startMs);
  const when = `${start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}, ${start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} – ${new Date(recording.endMs).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
  return (
    <div className="recordings__row">
      <div className="recordings__info">
        <strong>{recording.title}{recording.subtitle ? ` — ${recording.subtitle}` : ""}</strong>
        <span>{[recording.channelName, when, detail].filter(Boolean).join(" · ")}</span>
      </div>
      <div className="recordings__actions">{children}</div>
    </div>
  );
}
